import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment/moment";
import Tippy from "@tippyjs/react";
import swal from "@sweetalert/with-react";
import CustomeModal from "../../component/modal-popup/customeModal";
import { useDispatch, useSelector } from "react-redux";
import { getAllBookingsByCompanyId } from "../../redux/actions/booking";
import { deleteAssignmentById } from "../../redux/actions/assignmentAction";

import PageSelector from "../../component/PageSelector";
import { RESET_REDUX_STATE } from "../../redux/constants";
import useDebounce from "../../Hook/useDebounce";
import Loader from "react-spinner-loader";
import {
  Super_Admin,
  Admin,
  Collaborator,
  Customer,
} from "../../constant/AuthenticationConstant";
import { ToggleFilter } from "../../component/Filter/CheckboxFilter";
import { DraggableTable } from "../../component/table";
import { getBookingFieldList } from "../../redux/actions/booking";
import {
  getCustomizedTableColumnsData,
  addOrEditCustomizedTableColumns,
} from "../../redux/actions/customizedTable";
import RowsPerPageSelector from "../../component/RowsPerPageSelector";

function BookingOverviewTable({
  searchText,
  page,
  setPage,
  sortField,
  setSortField,
  sortOrder,
  setSortOrder,
}) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { bookingList, bookingFieldList } = useSelector(
    (state) => state.booking
  );
  const user = useSelector((state) => state.auth);

  const [loader, setLoader] = useState(false);

  const isCustomerOrCollaborator = ["customer", "collaborator"].includes(
    user.type
  );

  // Function to display table header and sort functionality
  const headerWithSort = (headerName, sort_by, isSort) => {
    return (
      <div>
        <span
          title={headerName}
          className="cursor-pointer truncate"
          onClick={() => {
            if (isSort) {
              handleHeaderClick(sort_by);
            }
          }}
        >
          {headerName}
        </span>
        {isSort &&
          !toggleData["Booking Data"]?.some(
            (item) => item.label === headerName
          ) && (
            <svg
              width="12"
              height="16"
              viewBox="0 0 8 12"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="inline ml-2 -mt-[2px]"
            >
              <path
                d="M6.82673 4.88075C6.69853 4.8809 6.57156 4.85571 6.45312 4.80662C6.33468 4.75754 6.22711 4.68553 6.13659 4.59474L3.89826 2.35647L1.65994 4.59474C1.47634 4.77505 1.22896 4.87555 0.971628 4.87439C0.714298 4.87322 0.46784 4.77048 0.285878 4.58852C0.103916 4.40655 0.00117537 4.1601 1.00221e-05 3.90277C-0.00115533 3.64544 0.0993491 3.39806 0.279655 3.21446L3.20812 0.285989C3.29874 0.195323 3.40633 0.1234 3.52476 0.0743293C3.64318 0.0252584 3.77011 0 3.89829 0C4.02648 0 4.15341 0.0252584 4.27183 0.0743293C4.39026 0.1234 4.49785 0.195323 4.58847 0.285989L7.51693 3.21446C7.65341 3.35098 7.74635 3.5249 7.784 3.71423C7.82164 3.90356 7.80231 4.09981 7.72843 4.27816C7.65456 4.4565 7.52947 4.60894 7.36897 4.7162C7.20847 4.82346 7.01977 4.88072 6.82673 4.88075Z"
                className={`cursor-pointer fill-current ${
                  sortField === sort_by && sortOrder === "asc"
                    ? "text-[--bg-color-500]"
                    : "text-[#9C9C9C]"
                }`}
                onClick={() => {
                  setSortOrder("asc");
                  setSortField(sort_by);
                }}
              />
              <path
                d="M3.56018 11.9259C3.67862 11.975 3.80559 12.0001 3.93379 12C4.062 12.0001 4.18897 11.975 4.30741 11.9259C4.42585 11.8768 4.53342 11.8048 4.62394 11.714L7.5524 8.78552C7.73355 8.60208 7.83477 8.35444 7.83396 8.09664C7.83316 7.83884 7.73039 7.59183 7.5481 7.40953C7.36581 7.22724 7.11881 7.12446 6.86101 7.12364C6.60321 7.12283 6.35556 7.22404 6.17212 7.40517L3.93379 9.6435L1.69547 7.40523C1.51187 7.22493 1.26449 7.12442 1.00716 7.12559C0.749827 7.12675 0.503369 7.22949 0.321407 7.41145C0.139445 7.59342 0.0367045 7.83987 0.0355392 8.09721C0.0343738 8.35454 0.134878 8.60191 0.315185 8.78552L3.24365 11.714C3.33417 11.8048 3.44174 11.8768 3.56018 11.9259Z"
                className={`cursor-pointer fill-current ${
                  sortField === sort_by && sortOrder === "desc"
                    ? "text-[--bg-color-500]"
                    : "text-[#9C9C9C]"
                }`}
                onClick={() => {
                  setSortOrder("desc");
                  setSortField(sort_by);
                }}
              />
            </svg>
          )}
      </div>
    );
  };

  // Function to handle toggle change
  const handleToggleChange = (index, key) => {
    setToggleData((prevToggleData) => {
      const updatedData = { ...prevToggleData };
      updatedData[key][index].checked = !updatedData[key][index].checked;
      return updatedData;
    });
  };

  // Function to check Custom Field
  const isCustomeField = (table_column_key) => {
    return bookingFieldList.some((item) => item.key === table_column_key)
  }

  // Draggable Table State
  const { customized_columns } = useSelector((state) => state.customizedTable);
  const [data, setData] = useState(() => []);
  const [toggleData, setToggleData] = useState({
    General: [
      {
        label: "Booking ID",
        value: "job_id",
        checked: true,
        size: 200,
      },
      {
        label: "Created At",
        value: "createdAt",
        checked: true,
        size: 200,
      },
      {
        label: "Created By",
        value: "createdBy",
        checked: true,
        size: 200,
      },
      {
        label: "Status",
        value: "status",
        checked: true,
        size: 200,
      },
      {
        label: "To Do Assignment",
        value: "ToDoAssignment",
        checked: true,
        size: 200,
      },
      {
        label: "Vendor",
        value: "vendor_name",
        checked: false,
        size: 200,
      },
      {
        label: "Customer",
        value: "customer",
        checked: false,
        size: 200,
      },
    ],
  });
  const [columns, setColumns] = useState([]);
  const [columnOrder, setColumnOrder] = useState([]);
  const [isTableChange, setIsTableChange] = useState(0);

  // Use the debounce hook to get the debounced version of the handleSearch function
  const debouncedHandleSearch = useDebounce(searchText, 300);

  useEffect(() => {
    if(debouncedHandleSearch){
      setPage(1);
    }
  }, [debouncedHandleSearch])

  // API call to get booking data
  useEffect(() => {
    setLoader(true);
    const request = {
      onSuccess: () => {
        setLoader(false);
      },
      onError: () => {
        setLoader(false);
      },
    };
    dispatch(
      getAllBookingsByCompanyId(
        debouncedHandleSearch,
        page,
        user.row_count,
        sortField,
        sortOrder,
        request
      )
    );
  }, [page, debouncedHandleSearch, sortOrder, user.row_count]);

  const _handleDelete = (assignmentid, id) => {
    const request = {
      onSuccess: () => {
        setLoader(false);
      },
      onError: () => {
        setLoader(false);
      },
    };
    dispatch(
      deleteAssignmentById(assignmentid, (res) => {
        if (res?.status === 1) {
          dispatch(
            getAllBookingsByCompanyId(
              searchText,
              page,
              user.row_count,
              sortField,
              sortOrder,
              request
            )
          );
        } else {
          swal({
            content: (
              <CustomeModal
                type="error"
                title="Error!"
                content={`Something went wrong. Please try again later.`}
              />
            ),
            buttons: false,
            closeOnClickOutside: false,
            closeOnEsc: false,
          });
        }
      })
    );
  };

  //Progress Bar
  const ProgressBar = (item) => {
    let completed = item.completed_activity;
    let total = item.total_activity;
    let tentative = item.tentative_activity;
    let progress = total === 0 ? 0 : completed / (total - tentative);
    return (
      <Tippy
        content={
          <div>
            <div class="my-0.5">Total To Dos: {total}</div>
            <div class="my-0.5">Assigned To Dos: {item.assigned_activity}</div>
            <div class="my-0.5">Completed To Dos: {completed}</div>
            <div class="my-0.5">Pending To Dos: {total - (completed + tentative)}</div>
            {/* <div class="my-0.5">Tentative To Dos: {tentative}</div> */}
          </div>
        }
      >
        <div class="w-full bg-gray-200 rounded-full dark:bg-gray-700">
          <div
            className="bg-[--bg-color-500] text-xs font-medium text-blue-100 text-center leading-none rounded-full"
            style={{
              width:
                parseInt(progress * 100) > 17
                  ? parseInt(progress * 100) + "%"
                  : "20%",
            }}
          >
            {parseInt(progress * 100)}%
          </div>
        </div>
      </Tippy>
    );
  };

  const handleHeaderClick = (sort_by) => {
    if (sortField === sort_by && sortOrder === "asc") {
      setSortOrder("desc");
      setSortField(sort_by);
    } else {
      setSortOrder("asc");
      setSortField(sort_by);
    }
  };

  // Function to call API to get booking field
  const getBookingFieldListData = () => {
    dispatch(getBookingFieldList());
  };

  // function to call API to get customized columns data added by user
  const getCustomizedTableColumns = () => {
    const request = {
      type: "booking",
    };
    dispatch(getCustomizedTableColumnsData(request));
  };

  // Function to call Post API to add table changes
  const addAndUpdateCustomizableTableChange = (size) => {
    let concatenatedArr;
    if (toggleData["Booking Data"]) {
      concatenatedArr = toggleData["General"].concat(
        toggleData["Booking Data"]
      );
    } else {
      concatenatedArr = toggleData["General"];
    }

    const table_structure = columnOrder
      .slice(0, -1)
      .map((columnField, index) => {
        const isColumnAvailable = concatenatedArr.find(
          (toggleField) => toggleField.value === columnField
        );
        const obj = {
          key: isColumnAvailable.value,
          label: isColumnAvailable.label,
          sequence: index + 1,
          size:
            size && Object.keys(size).length > 0
              ? Object.keys(size).some((key) => key === isColumnAvailable.value)
                ? size[isColumnAvailable.value]
                : isColumnAvailable.size
              : isColumnAvailable.size,
        };
        return obj;
      });

    const payload = {
      type: "booking",
      table_structure: JSON.stringify(table_structure),
    };

    if (customized_columns) {
      payload.table_layout_id = customized_columns.table_layout_id;
    }

    const request = {
      payload,
    };

    dispatch(addOrEditCustomizedTableColumns(request));

    setIsTableChange(0);
  };

  useEffect(() => {
    if (user.type !== Collaborator && user.type !== Customer) {
      getBookingFieldListData();
      getCustomizedTableColumns();
    }
  }, []);

  // Use the debounce hook to get the debounced version of the columns changes in customizable table
  const debounceHandleColumnsChange = useDebounce(isTableChange, 5000);

  //  useEffect to call API to upsert columns changes in customizable table
  useEffect(() => {
    if (debounceHandleColumnsChange) {
      addAndUpdateCustomizableTableChange();
    }
  }, [debounceHandleColumnsChange]);

  // useEffect to add booking custom fields
  useEffect(() => {
    if (bookingFieldList?.length > 0) {
      const customFieldToogle = bookingFieldList.map((field) => {
        const toogleObj = {
          label: field.label,
          value: field.key,
          checked: customized_columns?.table_structure
            ? JSON.parse(customized_columns.table_structure).some(
                (element) => element.key === field.key
              )
            : false,
          // size: 200,
          size: customized_columns?.table_structure
            ? JSON.parse(customized_columns.table_structure).some(
                (element) => element.key === field.key
              )
              ? JSON.parse(customized_columns.table_structure).find(
                  (element) => element.key === field.key
                ).size
              : 200
            : 200,
        };
        return toogleObj;
      });

      if (customized_columns?.table_structure) {
        const table_structure_data = JSON.parse(
          customized_columns.table_structure
        );
        const generalFieldToggle = toggleData["General"].map((field) => {
          const toogleObj = {
            label: field.label,
            value: field.value,
            checked: table_structure_data.some(
              (element) => element.key === field.value
            ),
            size: table_structure_data.some(
              (element) => element.key === field.value
            )
              ? table_structure_data.find(
                  (element) => element.key === field.value
                ).size
              : 200,
          };
          return toogleObj;
        });

        setToggleData({
          General: generalFieldToggle,
          "Booking Data": customFieldToogle,
        });
      } else {
        setToggleData({ ...toggleData, "Booking Data": customFieldToogle });
      }
    }
  }, [bookingFieldList, customized_columns]);

  // useEffect to setting columns in table
  useEffect(() => {
    const columnsData = [];

    if (customized_columns?.table_structure) {
      const table_structure_data = JSON.parse(
        customized_columns.table_structure
      );

      // Added two General and Booking Data fields
      let concatenatedArr;
      if (toggleData["Booking Data"]) {
        concatenatedArr = toggleData["General"].concat(
          toggleData["Booking Data"]
        );
      } else {
        concatenatedArr = toggleData["General"];
      }

      // Filter concatenatedArr
      const filterConcatenateArr = concatenatedArr.filter(
        (element) => element.checked
      );

      for (const table_column of table_structure_data) {
        if (
          filterConcatenateArr.some((item) => item.value === table_column.key)
        ) {
          columnsData.push({
            accessorKey: table_column.key,
            cell: (info) => info.getValue(),
            id: table_column.key,
            header:
              table_column.label === "Status" ||
              table_column.label === "To Do Assignment" || isCustomeField(table_column.key)
                ? headerWithSort(table_column.label, table_column.key, false)
                : headerWithSort(table_column.label, table_column.key, true),
            minSize: 50,
            size: table_column.size,
          });
        }
      }

      const filteredNotAddedField = filterConcatenateArr.filter(
        (filterItem) =>
          !table_structure_data.some(
            (tableItem) => tableItem.key === filterItem.value
          )
      );

      // Added fields which is not in table_structure_data but it is checked by user
      if (filteredNotAddedField.length > 0) {
        for (const filteredField of filteredNotAddedField) {
          columnsData.push({
            accessorKey: filteredField.value,
            cell: (info) => info.getValue(),
            id: filteredField.value,
            header:
              filteredField.label === "Status" ||
              filteredField.label === "To Do Assignment" || isCustomeField(filteredField.value)
                ? headerWithSort(
                    filteredField.label,
                    filteredField.value,
                    false
                  )
                : headerWithSort(
                    filteredField.label,
                    filteredField.value,
                    true
                  ),
            minSize: 50,
            size: 200,
          });
        }
      }
    } else {
      Object.keys(toggleData).map((toggleKey, index) => {
        for (const toggleVal of toggleData[toggleKey]) {
          if (toggleVal.checked) {
            columnsData.push({
              accessorKey: toggleVal.value,
              cell: (info) => info.getValue(),
              id: toggleVal.value,
              header:
                toggleKey === "Booking Data" ||
                toggleVal.label === "Status" ||
                toggleVal.label === "To Do Assignment" || isCustomeField(toggleVal.value)
                  ? headerWithSort(toggleVal.label, toggleVal.value, false)
                  : headerWithSort(toggleVal.label, toggleVal.value, true),
              minSize: 50,
              size: 200,
            });
          }
        }
        return null;
      });
    }

    columnsData.push({
      accessorKey: "action",
      cell: (info) => info.renderValue(),
      id: "action",
      header: (
        <div className="flex items-center justify-between z-60">
          <h1>Action</h1>
          {user.type === Super_Admin && (
            <ToggleFilter
              toggleData={toggleData}
              handleToggleChange={handleToggleChange}
              isTableChange={isTableChange}
              setIsTableChange={setIsTableChange}
            />
          )}
        </div>
      ),
      minSize: 50,
      size: 100,
    });

    if (isCustomerOrCollaborator) {
      columnsData.length = 0;
      for (const toggleVal of toggleData["General"]) {
        if (
          toggleVal.label !== "To Do Assignment" &&
          toggleVal.label !== "Created By" &&
          toggleVal.label !== "Vendor"
        ) {
          columnsData.push({
            accessorKey: toggleVal.value,
            cell: (info) => info.getValue(),
            id: toggleVal.value,
            header:
              toggleVal.label === "Status"
                ? headerWithSort(toggleVal.label, toggleVal.value, false)
                : headerWithSort(toggleVal.label, toggleVal.value, true),

            minSize: 50,
            size: 200,
          });
        }
      }
      columnsData.splice(1, 0, {
        accessorKey: "vendor_name",
        cell: (info) => info.getValue(),
        id: "vendor_name",
        header: headerWithSort("Vendor", "vendor_name", true),
        minSize: 50,
        size: 200,
      });
    }

    setColumns(columnsData);
  }, [sortField, sortOrder, toggleData]);

  useEffect(() => {
    if (columns.length > 0) {
      setColumnOrder(columns.map((c) => c.id));
    }
  }, [columns]);

  // Creating data
  useEffect(() => {
    if (bookingList?.booking?.length > 0) {
      const tableData = bookingList?.booking.map((item) => {
        const unassigned_activity =
          item.total_activity - item.assigned_activity; 
        const obj = {};
        obj.assignment_id = item.activity_assignment_id;

        // Adding data for general table columns and adding undefined for Booking Data table column
        for (const columnData of columns) {
          if (columnData.id === "createdAt") {
            obj[columnData.id] = moment(item[columnData.id]).format(
              "DD MMM, YYYY - HH:mm a"
            );
          } else {
            obj[columnData.id] = item[columnData.id];
          }
        }

        // Adding data to Booking Data table component
        if (toggleData["Booking Data"]) {
          for (const bookingDataColumns of toggleData["Booking Data"]) {
            if (bookingDataColumns.checked) {
              const custom_field_val = item.booking_controls.find(
                (comp) => comp.key === bookingDataColumns.value
              );

              obj[bookingDataColumns.value] = custom_field_val
                ? custom_field_val.value
                : "";
            }
          }
        }

        obj.action = (
          <div className="space-x-3 flex items-center">
            <div title="Assignment">
              <span
                className="fa-light fa-list-check text-base cursor-pointer"
                style={{ color: "#9297a2" }}
                onClick={(e) => {
                  e.stopPropagation();
                  dispatch({ type: RESET_REDUX_STATE });
                  navigate(`/assignment/assign/${item.activity_assignment_id}`);
                }}
              ></span>
            </div>
            <div title="Edit">
              <span
                className="fa-light fa-pen text-base cursor-pointer"
                style={{ color: "#9297a2" }}
                onClick={(e) => {
                  e.stopPropagation();
                  if (item.booking_template_id !== null) {
                    navigate("/booking/update", {
                      state: {
                        activity_assignment_id: item.activity_assignment_id,
                      },
                    });
                  }
                }}
              ></span>
            </div>
            <div title="Delete">
              <span
                className="fa-light fa-trash-can text-base cursor-pointer"
                style={{ color: "#eb5757" }}
                onClick={(e) => {
                  e.stopPropagation();
                  swal({
                    content: (
                      <CustomeModal
                        type="delete"
                        title="Are you sure?"
                        content={`All of your data will be permanently removed from our servers forever. This action cannot be undone.`}
                        onDelete={() =>
                          _handleDelete(
                            item.activity_assignment_id,
                            item.booking_id
                          )
                        }
                      />
                    ),
                    buttons: false,
                    closeOnClickOutside: false,
                    closeOnEsc: false,
                  });
                }}
              ></span>
            </div>
          </div>
        );
        obj.status = (
          <div className="flex justify-center items-center">
            {ProgressBar(item)}
          </div>
        );
        obj.ToDoAssignment =
          unassigned_activity > 0 ||
          (item.total_activity === 0 && item.assigned_activity === 0) ? (
            <div className="flex flex-row items-center">
              <div>
                <div className="w-4 h-4 border-3 rounded-full border-[--bg-color-500]"></div>
              </div>
              <p
                className="pl-2 text-sm font-normal tracking-tight leading-3 max-w-1/2 truncate"
                title="Pending"
              >
                Pending
              </p>
            </div>
          ) : (
            <div className="flex flex-row items-center">
              <div>
                <div className="w-4 h-4 border-3 rounded-full border-[--bg-color-500] bg-[--bg-color-500]"></div>
              </div>
              <p
                className="pl-2 text-sm font-normal tracking-tight leading-3 max-w-1/2 truncate"
                title="Completed"
              >
                Completed
              </p>
            </div>
          );
        return obj;
      });

      setData(tableData);
    }
  }, [bookingList, columns]);

  return (
    <div className="space-y-1">
      <DraggableTable
        data={data}
        columns={columns}
        columnOrder={columnOrder}
        setColumnOrder={setColumnOrder}
        loader={loader}
        noRecords={bookingList?.booking?.length === 0}
        isTableChange={isTableChange}
        setIsTableChange={setIsTableChange}
        ApiCallingFunction={addAndUpdateCustomizableTableChange}
      />
      {!loader && (
        <div className="flex justify-between items-center">
          <RowsPerPageSelector />
          <PageSelector
            rowPerPage={user.row_count}
            page={page}
            setpage={(newPage) => {
              setPage(newPage);
            }}
            totalDataCount={bookingList?.totalCount}
          />
        </div>
      )}
    </div>
  );
}
export default BookingOverviewTable;
